import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { borderRadius, palette } from '../../assets/styles/themes/tokens';
import SubscribeIcon from '../../assets/subscription/subscribe.svg';
import { selectUser } from '../../reducers/user.selectors';
import useRouter from '../../utils/useRouter';
import ReaderBanner from '../CustomBanner';
import Benefits from '../PricingPackage/Benefits';
import { getDownloadBannerProps } from './utils/downloadBannerUtils';
import {
	type AdditionalInfoType,
	getUpgradeBannerProps,
	PremiumBenefits,
	type UpgradeBannerProps,
} from './utils/upgradeBannerUtils';

interface ReaderBannerContainerProps {
	index: number;
	downloadMindItem: () => void;
	isReadOnly?: boolean;
}

const ReaderBannerContainer: React.FC<ReaderBannerContainerProps> = ({
	isReadOnly,
	index,
	downloadMindItem,
}) => {
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const { router } = useRouter();
	let bannerProps:
		| ReturnType<typeof getDownloadBannerProps>
		| (UpgradeBannerProps & { additionalInfo: React.ReactNode });

	if (!user) return null;

	const getAdditionalInfo = (type: AdditionalInfoType) => {
		let additionalInfo = null;
		if (type === 'premium') {
			additionalInfo = <Benefits benefit={PremiumBenefits} />;
		} else if (type === 'course') {
			additionalInfo = (
				<Typography variant="body1" sx={{ color: palette.text.secondary }}>
					{t(
						'You need meinUnterricht 360 for this document. Currently, you cannot adjust your subscription yourself—' +
							'but our support team will be happy to help.'
					)}
				</Typography>
			);
		}
		return additionalInfo;
	};

	if (!isReadOnly && index === 1) {
		bannerProps = getDownloadBannerProps(user, downloadMindItem, router, t);
	} else {
		const upgradeBannerProps = getUpgradeBannerProps(user, router, SubscribeIcon, t);

		bannerProps = {
			...upgradeBannerProps,
			additionalInfo: getAdditionalInfo(upgradeBannerProps.additionalInfoType),
		};
	}

	return (
		<Box
			sx={{
				position: 'absolute',
				height: '100%',
				width: '100%',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}}
		>
			<ReaderBanner
				open={true}
				{...bannerProps}
				dialogProps={{
					disablePortal: true,
					disableEscapeKeyDown: true,
					disableAutoFocus: true,
					disableEnforceFocus: true,
					sx: { position: 'absolute' },
					slotProps: {
						backdrop: {
							sx: {
								borderRadius: borderRadius.sm,
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							},
						},
					},
				}}
			/>
		</Box>
	);
};

export default ReaderBannerContainer;
